/**
 * inboundDatevApi — FE-Client für den Eingangsbelege-DATEV-Belegtransfer (IDV-6,
 * architecture-inbound-datev.md §5.5/§6, „Ziel 2").
 *
 * Ruft dieselbe `buildDatevBelegePaket`-HTTP-Function auf wie der Bestands-Belege-
 * Export (M7b, `api/datevBelegeApi.ts::downloadDatevBelegePaket`) — identisches
 * Muster (HTTP-`fetch` + manueller Bearer-Token statt `httpsCallable`, da die ZIP
 * > 10 MB werden kann; s. `datevBelegeApi.ts`-Kopfkommentar). Eigenständiges Modul
 * statt Wiederverwendung von `downloadDatevBelegePaket`, weil dessen Request-/
 * Result-Typen an `DatevDocumentRef` (`invoices`/`expenses`) gebunden sind — der
 * Vertrag hier (`InboundBelegRefInput`, `{ skipped, skippedKeys }` OHNE
 * `skippedDetail`) folgt exakt der in architecture-inbound-datev.md §5.5
 * deklarierten Signatur (Coder-Scope: NUR IDV-6-Dateien, `datevBelegeApi.ts` bleibt
 * unangetastet).
 *
 * @module inboundDatevApi
 */

import { getAuthToken } from './authUtils';
import type { DatevHeaderConfig } from '../utils/datevExport';
import type { InboundBelegRefInput } from '../utils/inboundDatevRefs';
import logger from '../utils/logger';

/**
 * Basis-URL der Firebase-HTTP-Functions (europe-west3). Muster `datevBelegeApi.ts`
 * (`REACT_APP_FUNCTIONS_URL`, sonst der produktive Projekt-Host).
 */
const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_URL || 'https://europe-west3-billution-invoices.cloudfunctions.net';

/** Vollständige URL der `buildDatevBelegePaket`-HTTP-Function (gemeinsamer Endpunkt, §6). */
const DATEV_BELEGE_PAKET_URL = `${FUNCTIONS_BASE_URL}/buildDatevBelegePaket`;

/**
 * Request-Body des Eingangsbelege-Belegtransfers (architecture-inbound-datev.md
 * §5.5). `refs` trägt NIE einen Storage-Pfad — die Function lädt Pfade autoritativ
 * aus dem jeweiligen `inbound_documents`-Doc (Sicherheits-Kernregel, M7b/IDV-5).
 */
export interface DownloadInboundDatevPaketRequest {
  fromISO: string;
  toISO: string;
  header: DatevHeaderConfig;
  refs: InboundBelegRefInput[];
}

/**
 * Ergebnis eines erfolgreichen Downloads: Anzahl + `documentKey`s der von der
 * Function best-effort übersprungenen Belege (`X-Datev-Belege-Skipped(-Keys)`-
 * Header) — exakte Signatur aus architecture-inbound-datev.md §5.5.
 */
export interface DownloadInboundDatevPaketResult {
  skipped: number;
  skippedKeys: string[];
}

/** Parst den `X-Datev-Belege-Skipped`-Header (`"0"` oder `"<n>; reason=k, …"`) → Zähler. */
function parseSkippedCount(raw: string | null): number {
  if (!raw) return 0;
  const countPart = raw.split(';')[0]?.trim() ?? '0';
  const count = Number.parseInt(countPart, 10);
  return Number.isFinite(count) ? count : 0;
}

/** Parst den `X-Datev-Belege-Skipped-Keys`-Header (komma-getrennte `documentKey`-Liste). */
function parseSkippedKeys(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((key) => key.trim())
    .filter((key) => key.length > 0);
}

/** Extrahiert den Dateinamen aus `Content-Disposition`, sonst `fallback`. */
function extractFilename(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return match?.[1] ?? fallback;
}

/**
 * Triggert einen Browser-Download eines ZIP-Blobs via Object-URL `<a download>`
 * (Muster `datevBelegeApi.ts::triggerZipDownload`).
 */
function triggerZipDownload(blob: Blob, fileName: string): void {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
}

/**
 * Ruft die `buildDatevBelegePaket`-HTTP-Function mit Eingangsbelege-Refs auf, lädt
 * die gestreamte ZIP als Blob und triggert den Browser-Download.
 *
 * @param req Zeitraum + Header + bereits gefilterte Refs (s. `buildInboundDatevRefs`).
 * @returns Anzahl + `documentKey`s der server-seitig übersprungenen Belege (best-effort).
 * @throws `Error` bei fehlendem Auth-Token, Netzwerkfehler oder Non-2xx-Response.
 */
export async function downloadInboundDatevPaket(
  req: DownloadInboundDatevPaketRequest,
): Promise<DownloadInboundDatevPaketResult> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Eingangsbelege-Übergabe: kein Auth-Token verfügbar. Bitte erneut anmelden.');
  }

  let response: Response;
  try {
    response = await fetch(DATEV_BELEGE_PAKET_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(req),
    });
  } catch (networkErr) {
    logger.error('inboundDatevApi: network error', networkErr);
    throw new Error('Eingangsbelege-Übergabe konnte nicht geladen werden (Netzwerkfehler).');
  }

  if (!response.ok) {
    let message = `Eingangsbelege-Übergabe fehlgeschlagen (HTTP ${response.status}).`;
    try {
      const errorBody = (await response.clone().json()) as { error?: string } | null;
      if (errorBody && typeof errorBody.error === 'string' && errorBody.error.length > 0) {
        message = errorBody.error;
      }
    } catch {
      // Response-Body war kein JSON — generische Meldung bleibt bestehen.
    }
    logger.error('inboundDatevApi: request failed', { status: response.status, message });
    throw new Error(message);
  }

  const skipped = parseSkippedCount(response.headers.get('X-Datev-Belege-Skipped'));
  const skippedKeys = parseSkippedKeys(response.headers.get('X-Datev-Belege-Skipped-Keys'));
  const fallbackName = `Eingangsbelege_${req.fromISO.replace(/-/g, '')}_${req.toISO.replace(/-/g, '')}.zip`;
  const fileName = extractFilename(response.headers.get('Content-Disposition'), fallbackName);

  const blob = await response.blob();
  triggerZipDownload(blob, fileName);

  return { skipped, skippedKeys };
}
